import React, { useEffect, useMemo, useState } from 'react';
import { Row } from './Row';
import { RowProps } from './models';
import SortHeader from './SortHeader';
import { Exclamation, Table } from './styles';
import LoaderComponent from 'features/shared/Loader';
import { ScrollWrapper } from 'features/monitoring/components/Table';
import useActivitiesTableData from './useActivitiesTableData';
import useUserData from 'hooks/useUserData';
import { StudentCountCell } from './StudentCountCell';
import { useClickStreamContext } from 'features/shared/ClickStream';
import { scrollToAncher } from 'features/shared/AnchorScroll';

interface Props {
  isRegion: boolean;
  abTest: boolean;
  activitiesTableData: ReturnType<typeof useActivitiesTableData>;
}
const Component = ({ isRegion, abTest, activitiesTableData }: Props) => {
  const { logPageEvent } = useClickStreamContext();
  const userData = useUserData();
  const { loaded, total, items, toggleSort, setStudentCount, sortConfig, hasInvolvementLowered } = activitiesTableData;

  return !loaded ? (
    <LoaderComponent />
  ) : (
    <>
      <ScrollWrapper>
        <Table>
          <thead>
            <tr>
              {isRegion ? (
                <>
                  <th rowSpan={2} style={{ width: '277px', paddingLeft: '16px', textAlign: 'start' }}>
                    Муниципалитеты
                  </th>
                  <th rowSpan={2} style={{ width: '83px', paddingLeft: '16px', textAlign: 'start' }}>
                    Колич.
                    <br />
                    школ
                  </th>
                </>
              ) : (
                <>
                  <th rowSpan={2} style={{ width: '196px', paddingLeft: '16px', textAlign: 'start' }}>
                    Населенный пункт
                  </th>
                  <th rowSpan={2} style={{ width: '164px', paddingLeft: '16px', textAlign: 'start' }}>
                    Школа
                  </th>
                </>
              )}
              <th colSpan={2} style={{ width: isRegion ? '264px' : '223px' }}>
                Учителя
              </th>
              <th colSpan={isRegion ? 2 : 3} style={{ width: isRegion ? '264px' : '305px' }}>
                Ученики
              </th>
            </tr>
            <tr>
              <th>
                <SortHeader
                  onClick={() => {
                    logPageEvent({
                      eventType: 'schools_active_school_sort_registered_teachers',
                      eventInfo: 'Школы|Активные школы: сортировка по столбцу зарегистрированные учителя',
                    });
                    toggleSort('registeredTeachers');
                  }}
                  direction={sortConfig?.key === 'registeredTeachers' ? sortConfig.direction : ''}
                >
                  Зарегистрир.
                  <br />с 1 сент 2025
                </SortHeader>
              </th>

              <th style={{ borderLeft: 'none' }}>
                <SortHeader
                  onClick={() => {
                    logPageEvent({
                      eventType: 'schools_active_school_sort_active_teachers',
                      eventInfo: 'Школы|Активные школы: сортировка по столбцу активные учителя',
                    });
                    toggleSort('activeTeachers');
                  }}
                  direction={sortConfig?.key === 'activeTeachers' ? sortConfig.direction : ''}
                >
                  Активн.
                </SortHeader>
              </th>
              {isRegion ? (
                <>
                  <th>
                    <SortHeader
                      onClick={() => {
                        logPageEvent({
                          eventType: 'schools_active_school_sort_registered_students',
                          eventInfo: 'Школы|Активные школы: сортировка по столбцу зарегистрированные ученики',
                        });
                        toggleSort('registeredStudents');
                      }}
                      direction={sortConfig?.key === 'registeredStudents' ? sortConfig.direction : ''}
                    >
                      Зарегистрир.
                      <br />с 1 сент 2025
                    </SortHeader>
                  </th>
                </>
              ) : (
                <>
                  <th>
                    <SortHeader
                      onClick={() => {
                        logPageEvent({
                          eventType: 'schools_active_school_sort_student_count',
                          eventInfo: 'Школы|Активные школы: сортировка по столбцу количество учеников',
                        });
                        toggleSort('studentCount');
                      }}
                      direction={sortConfig?.key === 'studentCount' ? sortConfig.direction : ''}
                    >
                      Всего
                    </SortHeader>
                  </th>
                  <th style={{ borderLeft: 'none' }}>
                    <SortHeader
                      onClick={() => {
                        logPageEvent({
                          eventType: 'schools_active_school_sort_registered_students',
                          eventInfo: 'Школы|Активные школы: сортировка по столбцу зарегистрированные ученики',
                        });
                        toggleSort('registeredStudents');
                      }}
                      direction={sortConfig?.key === 'registeredStudents' ? sortConfig.direction : ''}
                    >
                      Зарегистрир.
                      <br />с 1 сент 2025
                    </SortHeader>
                  </th>
                </>
              )}

              <th style={{ borderLeft: 'none' }}>
                <SortHeader
                  onClick={() => {
                    logPageEvent({
                      eventType: 'schools_active_school_sort_active_students',
                      eventInfo: 'Школы|Активные школы: сортировка по столбцу активные ученики',
                    });
                    toggleSort('activeStudents');
                  }}
                  direction={sortConfig?.key === 'activeStudents' ? sortConfig.direction : ''}
                >
                  Активн.
                </SortHeader>
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((school) => (
              <Row
                {...school}
                isRegion={isRegion}
                setStudentCount={setStudentCount}
                subjectName={userData?.department}
                abTest={abTest}
              />
            ))}
          </tbody>
          <tfoot style={{ fontWeight: 'bold' }}>
            <tr>
              {isRegion ? (
                <>
                  <td>Итого:</td>
                  <td>{total.school_count}</td>
                </>
              ) : abTest && hasInvolvementLowered ? (
                <>
                  <td>Итого:</td>
                  <td>
                    {items.filter((el) => el.involvementLowered).length}
                    <Exclamation style={{ marginLeft: 2, marginBottom: -3 }} />{' '}
                  </td>
                </>
              ) : (
                <td colSpan={2}>Итого:</td>
              )}
              <td>{total.reg_teacher_count}</td>
              <td style={{ borderLeft: 'none' }}>{total.active_teacher_count}</td>
              {isRegion ? (
                <>
                  <td>{total.reg_student_count}</td>
                </>
              ) : (
                <>
                  <td>
                    <StudentCountCell
                      studentCount={total.student_count}
                      setStudentCount={setStudentCount}
                      subjectName={userData?.department}
                    />
                  </td>
                  <td style={{ borderLeft: 'none' }}>{total.reg_student_count}</td>
                </>
              )}

              <td style={{ borderLeft: 'none' }}>{total.active_student_count}</td>
            </tr>
          </tfoot>
        </Table>
      </ScrollWrapper>
      {abTest && hasInvolvementLowered ? (
        <div style={{ marginTop: 16 }}>
          <Exclamation style={{ marginLeft: 2, marginBottom: -3 }} /> — активность упала более чем на 30% по сравнению с
          лучшим показатем прошлого полугодия
        </div>
      ) : null}
    </>
  );
};

export default Component;
